import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { Combobox } from './Combobox';
import { useInfiniteCustomerOptions } from '../service/customer';
import type { CustomerStoreT } from '../store/useCustomerStore';

type SelectedCustomer = CustomerStoreT['currentCustomer'];

interface CustomerComboboxProps {
  customer: SelectedCustomer | string;
  onSelectCustomer: (customer: SelectedCustomer) => void;
  onChange: (value: string) => void;
  className?: string;
}

export function CustomerCombobox({
  customer,
  onSelectCustomer,
  onChange,
  className,
}: CustomerComboboxProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);
  const query = useInfiniteCustomerOptions(debouncedSearch.trim());
  const customers = useMemo(() => {
    const rows = query.data?.pages.flatMap(page => page.customerResponse) ?? [];
    return [...new Map(rows.map(row => [String(row.id), row])).values()];
  }, [query.data]);
  const options = useMemo(
    () =>
      customers.map(row => ({
        value: String(row.id),
        label: `${row.username} ( ${row.id} )`,
      })),
    [customers]
  );
  const searching =
    search !== debouncedSearch ||
    (query.isFetching && !query.isFetchingNextPage);
  const selectedLabel =
    typeof customer === 'string'
      ? customer
      : customer
        ? `${customer.username || customer.name || ''} ( ${customer.id} )`
        : '';

  return (
    <Combobox
      allowAddOption={!query.isError && !searching && !query.isPending}
      className={className}
      options={options}
      value={
        typeof customer === 'string' ? customer : String(customer?.id ?? '')
      }
      selectedLabel={selectedLabel}
      onChange={value => {
        const selected = customers.find(row => String(row.id) === value);
        if (selected) {
          onSelectCustomer({
            ...selected,
            id: String(selected.id),
            trn: selected.trn ?? '',
          });
        } else {
          onChange(value);
        }
      }}
      placeholder="Choose Customer"
      useApiFilter
      onSearchChange={setSearch}
      loading={searching || query.isPending}
      hasMore={query.hasNextPage}
      loadingMore={query.isFetchingNextPage}
      onLoadMore={() => {
        if (!searching && !query.isFetching && query.hasNextPage) {
          void query.fetchNextPage({ cancelRefetch: false });
        }
      }}
      loadError={query.isError ? 'Unable to load customers.' : undefined}
      onRetry={() => {
        if (query.isFetchNextPageError) void query.fetchNextPage();
        else void query.refetch();
      }}
    />
  );
}
